
import React, { useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'

import { TagsIcon, ShoppingCartIcon, InfoIcon } from 'lucide-react'

import { Stack, Typography } from '@mui/material'

import { Table, EmptyState, StatusChipCell, LinkCell, ActionDropdown, ProductGroupCell, UserCell, AddressCell, IntroDataBanner, ExampleKindBadge, type ActionDropdownItem } from 'components'
import { ROUTES } from 'routes'
import { toDollar, date, parseShopifyGid } from 'utils'
import { ORDER_STATUSES } from 'constants/statuses'
import { statusFilterOptions } from 'components/input/filters/StatusChipFilter'
import { useReferrerState } from 'hooks'
import { useTagOrders, type TagShopOrderItem } from 'hooks/api/useTagOrders'
import { useCart } from 'contexts/CartContext'

export const Orders = () => {
  const navigate = useNavigate()
  // Derived from the Tags page URL this tab renders under — "← Back to Tags".
  const referrer = useReferrerState()
  const { addItem } = useCart()

  const { data, isLoading } = useTagOrders()
  const orders = data?.orders ?? []

  /**
   * "Order again" — reconstruct CartItems from the saved Shopify line items.
   *
   * CartItem.productId encodes "<numericShopifyProductId>#<numericShopifyVariantId>".
   * item.productId is now populated from the Shopify Admin API product.id GID
   * (VERA-847 fix). When absent (older orders), falls back to variantId for
   * both halves. A line item with no variant id at all is skipped; if no items
   * are added we navigate to the shop instead of the empty cart.
   */
  const handleOrderAgain = useCallback((order: TagShopOrderItem) => {
    let anyAdded = false

    for (const item of order.lineItems ?? []) {
      const rawVariantId = item.variant?.id
      if (!rawVariantId) continue

      const variantId = parseShopifyGid(rawVariantId)

      // Use the product id from the API when present (fixed in VERA-847).
      // Fall back to the variant id for both halves when the product id is
      // missing (older orders where the API did not return product.id).
      const resolvedProductId = item.productId || variantId
      const productId = `${resolvedProductId}#${variantId}`

      addItem({
        productId,
        variantId,
        sku: item.variant?.sku ?? '',
        title: item.title,
        variantTitle: item.variant?.title ?? item.title,
        unitPrice: Number(item.variant?.price ?? 0),
        casePrice: Number(item.variant?.price ?? 0),
        quantity: item.quantity ?? 1,
      })
      anyAdded = true
    }

    // If no line item had a valid variant id, navigate to the shop instead of
    // the cart (cart would be empty and confusing).
    navigate(anyAdded ? ROUTES.TAGS_SHOP_CART : ROUTES.TAGS_SHOP)
  }, [addItem, navigate])

  const getActionItems = useCallback((row: TagShopOrderItem) => {
    /*
      VRG-512 — a seeded sample order offers NEITHER action, and both omissions are
      §46 record-state hides rather than permission disables: the reason is a property
      of this row, the user's role has nothing to do with it, and there is no sentence
      a tooltip could offer that the badge on the row does not already say.

      Details    the row lives in the local `orders` table, not in Shopify.
                 `GET /shop/tags/orders/:id` reads the Shopify Admin API and would 404.
      Order again  the row has no Shopify variant (the API mapper leaves `variant.id`
                 empty deliberately — see `mapIntroOrderRowToDto`), so this would put a
                 fabricated line into the brand's REAL cart, one click from a real
                 checkout. `handleOrderAgain` already skips variant-less lines and would
                 land on an empty cart; hiding it means the brand is never offered a
                 no-op dressed as an action.

      §46's obligation — hiding an action obliges the app to make the RESOLVING action
      reachable in that state — is met by the `IntroDataBanner` directly above this
      table, which is where "clear the sample data" lives. That is what makes hiding
      right here rather than a disabled row explaining a dead end.
    */
    if (row.example_kind) return []

    const items: ActionDropdownItem[] = [
      {
        label: 'Details',
        // `parseShopifyGid`, NOT the raw `row.id`. That id is a full Shopify GID
        // (`gid://shopify/Order/7611697234218`), whose slashes split the ROUTE — it navigated to
        // `/tags/orders/gid:/shopify/Order/7611697234218` and rendered Page not found. The ID
        // cell in this same table already parsed it (see the `renderCell` below); only this
        // kebab action passed it through whole, so the row was reachable by clicking the id and
        // dead by using its own Details action.
        onClick: () => navigate(ROUTES.TAGS_ORDER(parseShopifyGid(row.id)), { state: referrer }),
        icon: <InfoIcon strokeWidth={1} />,
      },
      {
        label: 'Order again',
        onClick: () => handleOrderAgain(row),
        icon: <ShoppingCartIcon strokeWidth={1} />,
      },
    ]
    return items
  }, [navigate, handleOrderAgain, referrer])

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        // 210, up from 190: a sample row now renders the number AND the `Sample data`
        // badge on one line (168px of content), which does not fit a 190px column's
        // 162px content box. Widened rather than re-stacking the badge — the stack is
        // what read as broken.
        width: 210,
        pinned: 'left',
        type: 'string',
        // `value` is the Shopify order GID (gid://shopify/Order/<n>). Show the numeric
        // id (#ORD-<n>) and route by it — otherwise the cell renders the full GID,
        // which overflows the column.
        renderCell: ({ value, row }: { value: string; row: TagShopOrderItem }) => {
          /*
            VRG-512 — a seeded sample row is NOT a link, and this branch comes first
            because the link it would otherwise render is broken twice over.

            `value` on a sample row is the local `orders` uuid, not a Shopify GID, so
            `parseShopifyGid` (which matches `/<digits>$`) returns the EMPTY STRING: the
            cell would read "#ORD-" and route to `/tags/orders/`. Even given a usable
            id, `GET /shop/tags/orders/:id` reads the Shopify Admin API and has no local
            fallback, so the destination would 404. Same §46 record-state reasoning as
            the kebab below.

            The order number itself is shown instead — same `body3`/500/noWrap treatment
            `LinkCell` gives it, minus the affordance, so the column keeps its rhythm
            without promising a page that is not there.
          */
          if (row.example_kind) {
            /*
              The badge sits INLINE, to the right of the number — it used to stack
              beneath it, which read as a wrapped value rather than as an annotation.

              ⚠️ MOVING IT ALONE WOULD HAVE MADE THINGS WORSE, and that is why this
              changed alongside the seeded content rather than on its own. The old
              sample number was `INTRO-<13-digit epoch>-<n>` — 166px of text at
              14px/500, against a 190px column with 28px of padding. Putting an 88px
              badge on the same line would have left 74px for a 166px string: the
              number would have truncated to roughly `INTRO-17886…`, which is worse
              than the wrap it replaced.

              So the order number was reshaped first (`INTRO_ORDERS`, v10) to the
              `#ORD-<n>` form the real branch below renders. Measured at 1280x800:
              76px number + 4px gap + 88px badge = 168px, inside the 182px content box
              of a 210px column. Neither truncates, which is the check §49 makes for a
              header and which a VALUE beside a badge needs just as much.

              `spacing={1}` (4px) because that is what every OTHER inline
              `ExampleKindBadge` in a table cell already uses — `ProductCell` and
              `CertificateCell` both annotate their metadata line at 4px. This was the
              one stacked instance in the app; matching their gap is the whole point of
              un-stacking it.
            */
            return (
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ minWidth: 0, maxWidth: '100%' }}
              >
                <Typography variant="body3" sx={{ fontWeight: 500, minWidth: 0 }} noWrap>
                  {row.name || '—'}
                </Typography>
                <ExampleKindBadge kind={row.example_kind} size="dense" />
              </Stack>
            )
          }

          // A real order: byte-identical to what shipped before samples existed. The
          // sample case is the branch above rather than a wrapper around this one, so
          // the 100% of rows that are real never gain a Stack for a badge 0% of them
          // carry.
          const numericId = parseShopifyGid(value)
          return <LinkCell link={`#ORD-${numericId}`} route={ROUTES.TAGS_ORDER(numericId)} />
        },
      },
      {
        field: 'createdAt',
        headerName: 'Order date',
        type: 'dateTime',
        width: 150,
        valueGetter: (value: string) => value && new Date(value),
        renderCell: ({ value }: { value: string | Date }) =>
          value ? date(value instanceof Date ? value.toISOString() : value) : '—',
      },
      {
        field: 'lineItems',
        headerName: 'Tag shop products',
        type: 'string',
        width: 220,
        renderCell: ({ row }: { row: TagShopOrderItem }) => {
          // Map real product lines to the shape ProductGroupCell expects. The
          // no-variant "Customization setup fee" line is excluded — the customization
          // belongs to its product's line item, not a separate product tile.
          // `sku` feeds the tooltip's second line; `quantity` drives the count badge.
          const products = (row.lineItems ?? [])
            .filter(item => item.variant?.id)
            .map(item => ({
              title: item.title,
              sku: item.variant?.title
                ? `Quantity: ${item.variant.title}`
                : item.variant?.sku || '',
              quantity: item.quantity,
              id: item.variant?.id ?? item.id,
              images: item.image ? [item.image] : [],
            }))
          return <ProductGroupCell products={products} route={ROUTES.TAGS_SHOP_PRODUCT} showQuantityBadge />
        },
      },
      {
        field: 'totalPriceSet',
        headerName: 'Total',
        type: 'number',
        width: 100,
        renderCell: ({ row }: { row: TagShopOrderItem }) =>
          toDollar(Number(row.totalPriceSet?.shopMoney?.amount ?? 0)),
      },
      {
        field: 'fulfillmentStatus',
        headerName: 'Fulfillment status',
        type: 'statusChip',
        options: statusFilterOptions(ORDER_STATUSES),
        width: 160,
        renderCell: ({ value }: { value: string }) => <StatusChipCell status={value} statuses={ORDER_STATUSES} />,
      },
      {
        field: 'trackingNumber',
        headerName: 'Tracking number',
        type: 'string',
        width: 200,
        renderCell: ({ row }: { row: TagShopOrderItem }) => {
          if (!row.trackingNumber) return '—'
          if (row.trackingUrl) {
            return (
              <a href={row.trackingUrl} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
                {row.trackingNumber}
              </a>
            )
          }
          return row.trackingNumber
        },
      },
      {
        field: 'customer',
        headerName: 'Ordered by',
        type: 'string',
        width: 240,
        renderCell: ({ row }: { row: TagShopOrderItem }) => {
          const { firstName, lastName, email } = row.customer ?? {}
          const name = [firstName, lastName].filter(Boolean).join(' ')
          if (!name && !email) return '—'
          return <UserCell user={{ name, email }} />
        },
      },
      {
        field: 'shippingAddress',
        headerName: 'Shipping address',
        type: 'string',
        width: 300,
        // The shared AddressCell, not a joined string. It renders the icon + label + address the
        // address book uses, so a shipping address looks the same everywhere it appears. It
        // already accepts the raw Shopify shape (`address1`/`zip`) alongside the canonical one,
        // so the row passes straight through. `link={false}` because this address belongs to the
        // ORDER — it is a record of where that box went, not a row in the address book to open.
        renderCell: ({ row }: { row: TagShopOrderItem }) => {
          const addr = row.shippingAddress
          if (!addr?.address1) return '—'
          return <AddressCell address={addr} link={false} />
        },
      },
      {
        field: 'financialStatus',
        headerName: 'Payment status',
        type: 'string',
        width: 140,
        hidden: true,
      },
      {
        field: 'actions',
        headerName: false,
        pinned: 'right',
        sortable: false,
        disableReorder: true,
        align: 'center',
        width: 54,
        // No kebab at all on a sample row — `getActionItems` returns [] for one, and a
        // dropdown whose menu is empty is a control that does nothing when clicked.
        renderCell: ({ row }: { row: TagShopOrderItem }) => {
          const items = getActionItems(row)
          if (items.length === 0) return null
          return <ActionDropdown items={items} />
        },
      },
    ],
    [getActionItems],
  )

  /*
    VRG-512 — does THIS list actually have sample rows on it right now?

    The banner is gated on the rows rather than on `hasIntroData`, which is the exact
    case `IntroDataBanner.surfaceHasSamples` exists for. This list is served from the
    live Shopify Admin API, and a Shopify-sourced order is real by construction
    (`mapRawOrderToDto` sets `example_kind: null` explicitly), so a tenant can hold
    intro data while every row on screen belongs to somebody who actually placed an
    order. Rendering off `hasIntroData` alone would caption those real orders as
    samples, which is a worse failure than staying silent.

    `false` while loading, NOT `undefined`: the prop's own doc says `undefined` means
    "no extra condition" and shows the banner, so forwarding a loading-state `undefined`
    would flash it in and back out. `isLoading` is the first-load flag, so a background
    refetch keeps the answer the visible rows already justify (§19).
  */
  const surfaceHasSamples = isLoading ? false : orders.some(order => Boolean(order.example_kind))

  return (
    <>
      {/*
        The sample-data caveat, in the same slot every other brand surface uses — above
        the table it qualifies. See `IntroDataBanner`'s own docblock for the two states.

        The table below is `fillBottom`, so `useFillBottom` re-measures the grid's own top
        and the banner appearing above it re-fills rather than overflowing; `topOffset`
        stays as the inert first-paint fallback (§22).
      */}
      <IntroDataBanner nouns="orders" surfaceHasSamples={surfaceHasSamples} />

      <Table
        fillBottom
        loading={isLoading}
        topOffset={254}
        rows={orders}
        columns={columns}
        showSelection={false}
        selectionDisabled={true}
        visibilityModel={{
          financialStatus: false,
        }}
        emptyState={
          <EmptyState
            title="No orders placed"
            text="Visit the tag shop to place an order to get started using Vera with your products"
            icon={<TagsIcon strokeWidth={1} size={32} />}
          />
        }
      />
    </>
  )
}
